import type { ReactNode } from 'react';

/**
 * The frames draw no in-page title: a screen's name is the topbar trail's
 * last crumb. `title` stays the page's `h1` for assistive tech only, and what
 * the frames do draw in this band -- a badge, a meta line, the page's actions
 * -- renders as one row.
 */
export function PageHeader(props: {
  title: string;
  description?: string;
  badge?: ReactNode;
  actions?: ReactNode;
}): ReactNode {
  const meta = props.badge !== undefined || props.description !== undefined;
  // `sr-only` is absolutely positioned, so a bare heading is no flex item
  // of `Page` and adds no gap band above the content.
  const heading = <h1 className="sr-only">{props.title}</h1>;
  if (!meta && props.actions === undefined) return heading;
  return (
    <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
      {heading}
      {meta && (
        <div className="flex items-center gap-2">
          {props.badge}
          {props.description !== undefined && (
            <p className="text-body text-muted-foreground max-w-[66ch]">{props.description}</p>
          )}
        </div>
      )}
      {props.actions !== undefined && (
        // No `shrink-0`: that stopped this row from shrinking at all, which
        // stopped it from ever reaching the width where `flex-wrap` breaks
        // it onto a second line, and it overflowed the viewport instead.
        <div data-slot="actions" className="flex flex-wrap items-center gap-2 md:ml-auto">
          {props.actions}
        </div>
      )}
    </header>
  );
}
